import {
  getDatePartsInTimezone,
  getHourInTimezone,
  getMinuteInTimezone,
} from '@/lib/scheduling/timezone';
import { cx } from '@/utils/cx';

interface TimeGridProps {
  /** Starting hour (0-23, or higher for next-day times) */
  startHour?: number;
  /** Ending hour (0-48 to allow spanning into next day) */
  endHour?: number;
  /** Children (event blocks) are positioned absolutely within the grid */
  children?: React.ReactNode;
  /** Additional className */
  className?: string;
  /** Inline styles */
  style?: React.CSSProperties;
  /** Compact mode */
  compact?: boolean;
  /** Base date for calculating next-day label when hours >= 24 */
  date?: Date;
  /** IANA timezone for displaying next-day date labels. REQUIRED — dates
   * always render in an explicit timezone, never the browser's. */
  timezone: string;
  /** Hour cycle for the left-axis hour labels. Defaults to 24-hour (org default). */
  hourCycle?: 12 | 24;
}

/**
 * Time grid component showing hour labels on the left
 * and providing positioning context for event blocks.
 */
export function TimeGrid({
  startHour = 8,
  endHour = 18,
  children,
  className,
  style,
  compact = false,
  date,
  timezone,
  hourCycle = 24,
}: TimeGridProps) {
  const hours = [];
  for (let h = startHour; h <= endHour; h++) {
    hours.push(h);
  }

  const totalHours = endHour - startHour;

  // Format hour for display (handles hours >= 24 for next-day times).
  // Respects hourCycle so the left axis matches the event blocks' format.
  const formatHour = (hour: number) => {
    const normalizedHour = hour % 24;
    if (hourCycle === 24) {
      return `${normalizedHour.toString().padStart(2, '0')}:00`;
    }
    if (normalizedHour === 0) {
      return '12 AM';
    }
    if (normalizedHour === 12) {
      return '12 PM';
    }
    if (normalizedHour < 12) {
      return `${normalizedHour} AM`;
    }
    return `${normalizedHour - 12} PM`;
  };

  // Compact mode uses the same labels as the standard formatter.
  const formatHourCompact = (hour: number) => formatHour(hour);

  // Format the next day's date for the midnight separator
  const formatNextDayDate = (baseDate: Date | undefined) => {
    if (!baseDate) {
      return '';
    }

    // Use timezone-aware date calculation to handle DST and month/year rollovers
    const { year, month, day } = getDatePartsInTimezone(baseDate, timezone);

    // Add 1 day with proper month/year rollover
    let nextDay = day + 1;
    let nextMonth = month;
    let nextYear = year;

    // Days in each month (non-leap year)
    const daysInMonth = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

    // Check for leap year
    const isLeapYear =
      (nextYear % 4 === 0 && nextYear % 100 !== 0) || nextYear % 400 === 0;
    if (isLeapYear) {
      daysInMonth[1] = 29;
    }

    if (nextDay > daysInMonth[nextMonth - 1]) {
      nextDay = 1;
      nextMonth += 1;
      if (nextMonth > 12) {
        nextMonth = 1;
        nextYear += 1;
      }
    }

    // Construct next day date in UTC to avoid timezone offset issues
    const nextDayDate = new Date(Date.UTC(nextYear, nextMonth - 1, nextDay));

    return nextDayDate.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      timeZone: 'UTC',
    });
  };

  return (
    <div className={cx('relative', className)} style={style}>
      {/* Hour rows */}
      {hours.map((hour, index) => {
        const topPercent = (index / totalHours) * 100;
        const isLast = index === hours.length - 1;
        const isMidnight = hour === 24;

        return (
          <div key={hour}>
            {/* Midnight date separator */}
            {isMidnight && date && (
              <div
                className='absolute left-0 right-0 flex items-center pointer-events-none'
                style={{ top: `${topPercent}%`, zIndex: 10 }}
                data-testid='midnight-separator'
              >
                <div className='w-12 pr-3' />
                <div className='flex-1 flex items-center'>
                  <div className='flex-1 border-t border-secondary' />
                  <span
                    className={cx(
                      'px-2 text-tertiary font-medium',
                      compact ? 'text-[11px]' : 'text-xs'
                    )}
                  >
                    {formatNextDayDate(date)}
                  </span>
                  <div className='flex-1 border-t border-secondary' />
                </div>
              </div>
            )}
            {/* Hour row */}
            <div
              className='absolute left-0 right-0 flex'
              style={{ top: `${topPercent}%` }}
            >
              {/* Hour label */}
              <div
                className={cx(
                  'flex-shrink-0 -mt-2 text-right whitespace-nowrap',
                  'w-12 pr-3'
                )}
              >
                <span
                  className={cx(
                    'text-tertiary font-medium',
                    compact ? 'text-[11px]' : 'text-xs'
                  )}
                >
                  {compact ? formatHourCompact(hour) : formatHour(hour)}
                </span>
              </div>
              {/* Horizontal line */}
              <div
                className={cx(
                  'flex-1 border-t',
                  isLast ? 'border-transparent' : 'border-secondary'
                )}
              />
            </div>
          </div>
        );
      })}

      {/* Vertical separator between time labels and event area */}
      <div
        className='absolute top-0 bottom-0 border-l border-secondary pointer-events-none'
        style={{ left: '48px', zIndex: 5 }}
      />

      {/* Event blocks container - children are positioned absolutely */}
      <div className='absolute inset-0'>{children}</div>
    </div>
  );
}

/**
 * Helper function to calculate position and height for an event
 * within the time grid.
 */
// eslint-disable-next-line react-refresh/only-export-components -- helper function co-located with component
export function calculateEventPosition(
  startTime: string,
  endTime: string,
  gridStartHour: number,
  gridEndHour: number,
  gridDate: Date | undefined,
  timezone: string
): { top: number; height: number } {
  const start = new Date(startTime);
  const end = new Date(endTime);

  let startMinutes =
    getHourInTimezone(start, timezone) * 60 +
    getMinuteInTimezone(start, timezone);
  let endMinutes =
    getHourInTimezone(end, timezone) * 60 + getMinuteInTimezone(end, timezone);

  // Handle events in extended (>24h) grids by calculating day offset from grid date
  // This ensures events on the next day are positioned correctly in grids that span midnight
  if (gridDate) {
    const gridParts = getDatePartsInTimezone(gridDate, timezone);
    const gridDateOnly = new Date(
      gridParts.year,
      gridParts.month - 1,
      gridParts.day
    );
    const startParts = getDatePartsInTimezone(start, timezone);
    const startDateOnly = new Date(
      startParts.year,
      startParts.month - 1,
      startParts.day
    );
    const endParts = getDatePartsInTimezone(end, timezone);
    const endDateOnly = new Date(
      endParts.year,
      endParts.month - 1,
      endParts.day
    );

    const startDaysDiff = Math.round(
      (startDateOnly.getTime() - gridDateOnly.getTime()) / (24 * 60 * 60 * 1000)
    );
    const endDaysDiff = Math.round(
      (endDateOnly.getTime() - gridDateOnly.getTime()) / (24 * 60 * 60 * 1000)
    );

    if (startDaysDiff > 0) {
      startMinutes += startDaysDiff * 24 * 60;
    }
    if (endDaysDiff > 0) {
      endMinutes += endDaysDiff * 24 * 60;
    }
  } else {
    // Fallback: Handle events spanning midnight relative to event start
    const startParts = getDatePartsInTimezone(start, timezone);
    const startDateOnly = new Date(
      startParts.year,
      startParts.month - 1,
      startParts.day
    );
    const endParts = getDatePartsInTimezone(end, timezone);
    const endDateOnly = new Date(
      endParts.year,
      endParts.month - 1,
      endParts.day
    );

    const daysDiff = Math.round(
      (endDateOnly.getTime() - startDateOnly.getTime()) / (24 * 60 * 60 * 1000)
    );
    if (daysDiff > 0) {
      endMinutes += daysDiff * 24 * 60;
    } else if (gridEndHour > 24 && startMinutes < gridStartHour * 60) {
      // Events entirely on the next day in extended grids without gridDate
      startMinutes += 24 * 60;
      endMinutes += 24 * 60;
    }
  }

  const gridStartMinutes = gridStartHour * 60;
  const gridEndMinutes = gridEndHour * 60;
  const totalGridMinutes = gridEndMinutes - gridStartMinutes;

  // Clamp to grid bounds
  const clampedStart = Math.max(startMinutes, gridStartMinutes);
  const clampedEnd = Math.min(endMinutes, gridEndMinutes);

  const top = ((clampedStart - gridStartMinutes) / totalGridMinutes) * 100;
  const height = ((clampedEnd - clampedStart) / totalGridMinutes) * 100;

  return { top, height };
}
